// Import stores for integration utilities
import useAuthStore from "./authStore";
import { useChatStore } from "./chatStore";
import useMindspaceStore from "./mindspaceStore";
import useQuickActionsStore from "./quickActionsStore";
import useGuidedFlowStore from "./guidedFlowStore";
import { useUIStore } from "./uiStore";

// Store exports
export { default as useAuthStore } from "./authStore";
export { useIsGuest } from "./authStore";
export type { UserStatus, MessageLimits } from "../types";
export type { ApiUrls } from "./authStore";

export { useChatStore } from "./chatStore";

export { default as useMindspaceStore } from "./mindspaceStore";
export type { Mindspace, ContextItem } from "../services/apiClientV2";

export { default as useQuickActionsStore } from "./quickActionsStore";
export type { CategoryType, CardType } from "./quickActionsStore";

export { default as useGuidedFlowStore } from "./guidedFlowStore";
export type {
  GuidedQuestion,
  FlowResponse,
  FlowStatus,
} from "./guidedFlowStore";

export { useUIStore } from "./uiStore";
export type { ChatViewMode } from "./uiStore";

export { useToastStore } from "./toastStore";
export type { Toast, ToastType } from "./toastStore";

// Debug utilities for QA - Task F3.1 requirement
export const debugUtils = {
  // Get guided flow state for QA debugging
  getGuidedFlowState: () => {
    const state = useGuidedFlowStore.getState();
    return {
      status: state.status,
      answers: state.answers,
      startTime: state.startTime,
      activeFlow: state.activeFlow
        ? {
            templateId: state.activeFlow.template.template_id,
            currentQuestionIndex: state.activeFlow.currentQuestionIndex,
            totalQuestions: state.activeFlow.template.questions.length,
            responses: state.activeFlow.responses.map((r) => ({
              questionIndex: r.questionIndex,
              questionText: r.question.text,
              userResponse: r.userResponse,
              timestamp: r.timestamp,
            })),
            contextCollected: state.activeFlow.contextCollected,
            isCompleted: state.activeFlow.isCompleted,
          }
        : null,
      progress: state.getProgress(),
      currentQuestion: state.getCurrentQuestion(),
      canGoBack: state.canGoBack(),
      sessionStorageState: (() => {
        try {
          const sessionData = sessionStorage.getItem(
            "smartie_guided_flow_state",
          );
          return sessionData ? JSON.parse(sessionData) : null;
        } catch {
          return "Invalid session data";
        }
      })(),
    };
  },

  // Expose guided flow actions for debugging
  guidedFlowActions: {
    saveToSession: () => useGuidedFlowStore.getState().saveToSession(),
    loadFromSession: () => useGuidedFlowStore.getState().loadFromSession(),
    clearSession: () => useGuidedFlowStore.getState().clearSession(),
    resetFlow: () => useGuidedFlowStore.getState().resetFlow(),
    getCurrentQuestion: () =>
      useGuidedFlowStore.getState().getCurrentQuestion(),
    getProgress: () => useGuidedFlowStore.getState().getProgress(),
    getAnswers: () => useGuidedFlowStore.getState().answers,
  },
};

// Type for window debug utilities
interface SmartieDebug {
  getGuidedFlowState: typeof debugUtils.getGuidedFlowState;
  guidedFlowActions: typeof debugUtils.guidedFlowActions;
}

declare global {
  interface Window {
    smartieDebug?: SmartieDebug;
  }
}

// Expose debug utilities to window for QA - Task F3.1 requirement
if (typeof window !== "undefined") {
  window.smartieDebug = {
    ...(window.smartieDebug || {}),
    getGuidedFlowState: debugUtils.getGuidedFlowState,
    guidedFlowActions: debugUtils.guidedFlowActions,
  };
}

// Integration utilities for coordinating between stores
export const storeIntegration = {
  // Initialize all stores for MVP 2.0 features
  initializeMVP2Stores: async () => {
    const mindspaceStore = useMindspaceStore.getState();
    const quickActionsStore = useQuickActionsStore.getState();

    // Initialize mindspaces (creates default if needed)
    if (!mindspaceStore.isInitialized) {
      await mindspaceStore.loadMindspaces();
    }

    // Then load quick action templates
    if (!quickActionsStore.isInitialized) {
      await quickActionsStore.loadTemplates();
    }
  },

  // Complete a guided flow and persist context to mindspace
  completeGuidedFlowWithContext: async () => {
    const guidedFlowStore = useGuidedFlowStore.getState();
    const mindspaceStore = useMindspaceStore.getState();

    if (
      !guidedFlowStore.activeFlow ||
      guidedFlowStore.status !== "collecting"
    ) {
      throw new Error("No active guided flow to complete");
    }

    // Generate final prompt from guided flow
    const finalPrompt = guidedFlowStore.completeFlow();

    // Get collected canonical context
    const collectedContext = guidedFlowStore.getCollectedContext();

    // Persist canonical context to mindspace
    if (Object.keys(collectedContext).length > 0) {
      await mindspaceStore.updateMultipleContextItems(collectedContext);
    }

    return {
      finalPrompt,
      contextPersisted: Object.keys(collectedContext).length,
      template: guidedFlowStore.activeFlow.template,
    };
  },

  // Reset all stores for a fresh session
  resetAllStores: () => {
    const guidedFlowStore = useGuidedFlowStore.getState();
    const mindspaceStore = useMindspaceStore.getState();
    const quickActionsStore = useQuickActionsStore.getState();

    // Reset guided flow
    guidedFlowStore.resetFlow();

    // Clear errors from other stores
    mindspaceStore.clearError();
    quickActionsStore.clearError();
  },

  // Get comprehensive state for debugging
  getDebugState: () => {
    return {
      auth: useAuthStore.getState(),
      chat: useChatStore.getState(),
      mindspace: useMindspaceStore.getState(),
      quickActions: useQuickActionsStore.getState(),
      guidedFlow: useGuidedFlowStore.getState(),
      ui: useUIStore.getState(),
    };
  },
};
